import React, { useEffect, useState } from 'react'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  currentMappingSetAtom,
  mappedPacingGuideRowsAtom,
  mappingSetsAtom,
} from './atoms'
import { Box, Button } from '@mui/material'
import {
  CrosswalkMappingSetType,
  StandardAlignmentResponseType,
} from 'domains/scope-and-sequence/types'
import { fetchUser } from 'shared/api/user'
import { useAtomWithErrorNotification } from 'shared/ui/GlobalDialog'
import { openTab } from 'components/Helpers'
import { ScopeAndSequenceRow } from 'domains/scope-and-sequence/features/parser/types'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import Tooltip from '@mui/material/Tooltip'
import { alternatingRowColor } from './utility'
import { PlainLink } from 'infrastructure/routing'
import { PercentStatusBar } from 'domains/scope-and-sequence/features/crosswalk/PercentStatusBar'
import { snakeKabobToTitleCase } from 'shared/utils'

export function CrosswalkDetails() {
  const mappingSetData = useAtomWithErrorNotification(
    { errorTitle: 'Error Requesting Crosswalks' },
    mappingSetsAtom,
  )
  const setMappingSet = useSetAtom(currentMappingSetAtom)

  useEffect(() => {
    if (!mappingSetData?.data?.length) {
      setMappingSet(undefined)
    } else if (mappingSetData?.data?.length === 1) {
      setMappingSet(mappingSetData?.data[0])
    }
  }, [mappingSetData, setMappingSet])

  if (!mappingSetData?.frameworksSelected)
    return <Box>Need to first select Frameworks</Box>

  if (!mappingSetData?.data) return <Box>Loading...</Box>

  if (!mappingSetData?.data.length) return <NoCrosswalksFound />

  return (
    <>
      <CrosswalkSelection crosswalks={mappingSetData.data} />
      <CurrentCrosswalk />
    </>
  )
}

function NoCrosswalksFound() {
  return (
    <Box>
      <Box>No Crosswalks Found</Box>
      <Box>
        <Button onClick={() => openTab('/scope_and_sequence/crosswalks')}>
          Open Crosswalk Parser
        </Button>
      </Box>
    </Box>
  )
}

function CrosswalkSelection({
  crosswalks,
}: {
  crosswalks: CrosswalkMappingSetType[]
}) {
  if (crosswalks.length === 1) return null
  return `${crosswalks.length} Crosswalks Found`
}

function CurrentCrosswalk() {
  const [createdBy, setCreatedBy] = useState<{ name: string; email: string }>()
  const currentCrosswalk = useAtomValue(currentMappingSetAtom)

  useEffect(() => {
    if (!currentCrosswalk?.created_by) {
      setCreatedBy(undefined)
      return
    }
    fetchUser(Number(currentCrosswalk.created_by)).then(setCreatedBy)
  }, [currentCrosswalk?.created_by])

  if (!currentCrosswalk) return null

  const targetInvalid = currentCrosswalk?.alignments?.some(
    (x) => !x.target_valid,
  )
  const sourceInvalid = currentCrosswalk?.alignments?.some(
    (x) => !x.source_valid,
  )
  return (
    <Box>
      <Box>
        <PlainLink to={`/crosswalkMappingSets/${currentCrosswalk.id}/show`}>
          {currentCrosswalk.name}
        </PlainLink>
      </Box>
      <Box
        sx={{
          padding: '8px',
          display: 'flex',
          gap: '8px',
          flexDirection: 'column',
        }}
      >
        <Box>{`Created By: ${createdBy?.name ?? currentCrosswalk.created_by}`}</Box>
        {targetInvalid && (
          <Box>⚠️ Target contains standards that are missing in Kiddom</Box>
        )}
        {sourceInvalid && (
          <Box>⚠️ Source contains standards that are missing in Kiddom</Box>
        )}
      </Box>
      <Box
        sx={{
          padding: '8px',
          display: 'flex',
          gap: '8px',
          flexDirection: 'column',
        }}
      >
        <Box fontWeight={600}>Alignment Types:</Box>
        <PercentStatusBar alignments={currentCrosswalk?.alignments} />
      </Box>
      <Box
        sx={{
          mt: 2,
          p: 2,
          backgroundColor: '#fff8e1',
          borderLeft: '4px solid #ffc107',
          borderRadius: 1,
          fontSize: 14,
          color: 'text.secondary',
        }}
      >
        This table shows your pacing guide with Common Core alignments applied
        from the selected crosswalk. The alignment bar indicates overall match
        quality between state standards and CCSS. Select &lsquo;Review
        Crosswalk&rsquo; to view full details or make edits.
      </Box>
      <CurrentCrosswalkTable />
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          href={`/crosswalkMappingSets/${currentCrosswalk.id}/show`}
        >
          Review Crosswalk
        </Button>
      </Box>
    </Box>
  )
}

function CurrentCrosswalkTable() {
  const alignments = useAtomValue(mappedPacingGuideRowsAtom)

  if (!alignments.length) return null

  const textColumns = [
    ...(
      [
        'grade',
        'unitName',
        'unitNumber',
        'standard',
      ] as (keyof ScopeAndSequenceRow)[]
    ).map((key) => {
      return {
        title: snakeKabobToTitleCase(key),
        rowContent(row: ScopeAndSequenceRow) {
          return row[key]
        },
        width: ['grade', 'unitNumber'].includes(key) ? 50 : undefined,
      }
    }),
    {
      title: 'Common Core Alignment',
      rowContent({ alignment }: { alignment?: StandardAlignmentResponseType }) {
        if (alignment) {
          return alignment.target_standard_code.replace(
            'CCSS.Math.Content.',
            '',
          )
        }
        return (
          <Tooltip title="The Pacing Guide includes standards not found in the Crosswalk mapping.">
            <Box color="red">MISSING</Box>
          </Tooltip>
        )
      },
    },
  ] as Column<
    ScopeAndSequenceRow & {
      alignment?: StandardAlignmentResponseType
    }
  >[]

  return (
    <Table
      title="Pacing Guide with Crosswalk Alignments"
      columns={textColumns}
      items={alignments}
      getRowStyle={(idx) => {
        const unitNum = alignments[idx].unitNumber
        return alternatingRowColor(unitNum)
      }}
    />
  )
}
